// ─── Option label resolution ────────────────────────────────────────────────
// Unica fonte di verità per "slug → etichetta visibile".
//
// Prima esistevano due percorsi divergenti per lo stesso valore: il form
// traduceva via i18n (options.<group>.<value>) mentre il Riepilogo e la griglia
// metadati dell'output mostravano rispettivamente la label grezza del backend e
// lo slug di wire. Risultato: "Drive Traffic" nei Parametri e "Aumentare il
// Traffico" nel Riepilogo per lo stesso obiettivo.
//
// Ordine di risoluzione (identico ovunque):
//   1. options.<group>.<value> dal namespace i18n `wizard`
//   2. la label dell'opzione, se il backend ne ha fornita una
//   3. il fallback ('—' di default)
//   Eccezione: il gruppo `tone` inverte l'ordine — vedi resolveToneLabel in fondo al file.

export const MISSING_LABEL = '—';

// Mappa un array di opzioni a label tradotte. `tr` è il t() del namespace wizard.
export const translateOptions = (tr, group, opts = []) =>
  (opts || []).map((o) => {
    const key = `options.${group}.${o.value}`;
    const translated = tr(key);
    return { ...o, label: translated === key ? (o.label ?? o.value) : translated };
  });

// Risolve UN valore. `opts` può essere omesso quando il gruppo è interamente
// coperto dall'i18n (es. i valori provenienti da entry.params nell'output).
export const translateOptionLabel = (tr, group, value, opts = [], fallback = MISSING_LABEL) => {
  if (value == null || value === '') return fallback;
  const key = `options.${group}.${value}`;
  const translated = tr(key);
  if (translated !== key) return translated;
  const found = (opts || []).find(o => o.value === value);
  return found?.label ?? fallback;
};

// Tone is the one group where the BACKEND label wins over i18n. The tone list is
// served per-tenant by GET /api/v1/context/tone-presets, so options.tone.* in the
// locale files is stale by construction (it still reads "Bold & Provocative" in
// both it and en). Priority: BE label → i18n → label → raw value.
//
// Only entries tagged source:'backend' get that priority. The FE-only `custom`
// entry and the whole TONE_OPTIONS fallback carry hardcoded Italian labels that
// would otherwise shadow the correct English i18n strings ('Custom*',
// 'Brand Voice (from KB)') exactly when the fallback is doing its job.
//
// The raw-value tail matters twice: for free-text tone_hint (which must stay
// readable verbatim) and for surfaces with no preset list — e.g. the superadmin
// run detail, where fetching presets would return the WRONG tenant's labels.
export const resolveToneLabel = (tr, value, presets = [], fallback = MISSING_LABEL) => {
  if (value == null || value === '') return fallback;
  const fromPreset = (presets || []).find(o => o.value === value);
  if (fromPreset?.source === 'backend' && fromPreset.label) return fromPreset.label;
  const key = `options.tone.${value}`;
  const translated = tr(key);
  if (translated !== key) return translated;
  return fromPreset?.label ?? value;
};
